import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Mail, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isIOS } from '../lib/capacitor';

export default function Login() {
  const [searchParams] = useSearchParams();
  const signupMode = searchParams.get('signup') === 'true';
  const planName = searchParams.get('plan');

  const [isSignUp, setIsSignUp] = useState(signupMode);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const { signIn, signUp, resetPassword } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await resetPassword(email);
      if (error) {
        setError(error.message);
      } else {
        toast.success('Password reset email sent! Check your inbox.');
        setIsForgotPassword(false);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (isSignUp) {
        const { error, data } = await signUp(email, password);
        if (error) {
          setError(error.message);
        } else {
          // If session exists, user is logged in immediately
          if (data?.session) {
            const pendingCheckout = localStorage.getItem('pendingCheckout');
            if (pendingCheckout && !isIOS()) {
              navigate('/pricing');
            } else {
              if (isIOS()) localStorage.removeItem('pendingCheckout');
              navigate('/');
            }
          } else if (data?.user) {
            // User created but no session - try auto sign-in
            const { error: signInError } = await signIn(email, password);
            if (!signInError) {
              const pendingCheckout = localStorage.getItem('pendingCheckout');
              if (pendingCheckout && !isIOS()) {
                navigate('/pricing');
              } else {
                if (isIOS()) localStorage.removeItem('pendingCheckout');
                navigate('/');
              }
            } else {
              // Email confirmation might be required
              setMessage('Check your email for a confirmation link!');
            }
          } else {
            setMessage('Check your email for a confirmation link!');
          }
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          // Check if there's a pending checkout - redirect to pricing to process it
          const pendingCheckout = localStorage.getItem('pendingCheckout');
          if (pendingCheckout && !isIOS()) {
            navigate('/pricing');
          } else {
            if (isIOS()) localStorage.removeItem('pendingCheckout');
            navigate('/');
          }
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (isForgotPassword) {
    return (
      <div className="app-container">
        <div className="app-wrapper">
          <div className="app-card">
            <div className="app-header">
              <div className="auth-mode-badge">RESET PASSWORD</div>
              <h1>
                <Mail className="header-icon" />
                Forgot Password?
              </h1>
              <p>Enter your email and we'll send you a reset link</p>
            </div>

            <div className="login-section">
              <form onSubmit={handleForgotPassword}>
                <div className="input-group">
                  <Mail size={18} className="input-icon" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    className="auth-input"
                    required
                  />
                </div>

                {error && <p className="auth-error">{error}</p>}

                <button type="submit" className="btn btn-yellow btn-large" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="auth-switch">
                <p>
                  Remember your password?
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(false);
                      setError('');
                    }}
                    className="link-btn"
                  >
                    Sign In
                  </button>
                </p>
              </div>

              <div className="back-link">
                <Link to="/">Back to App</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="app-wrapper">
        <div className="app-card">
          <div className={`app-header ${isSignUp ? 'signup-header' : ''}`}>
            <div className={`auth-mode-badge ${isSignUp ? 'signup-badge' : 'signin-badge'}`}>
              {isSignUp ? 'NEW ACCOUNT' : 'SIGN IN'}
            </div>
            <h1>
              {isSignUp ? <User className="header-icon" /> : <Lock className="header-icon" />}
              {isSignUp ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p>
              {isSignUp
                ? (planName ? `Sign up to subscribe to ${planName}` : 'Sign up to get started')
                : 'Sign in to your account'}
            </p>
          </div>

          <div className="login-section">
            <form onSubmit={handleSubmit}>
              <div className="input-group">
                <Mail size={18} className="input-icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="auth-input"
                  required
                />
              </div>

              <div className="input-group">
                <Lock size={18} className="input-icon" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="auth-input"
                  required
                  minLength={6}
                />
              </div>

              {error && <p className="auth-error">{error}</p>}
              {message && <p className="auth-success">{message}</p>}

              <button type="submit" className={`btn btn-large ${isSignUp ? 'btn-green' : 'btn-yellow'}`} disabled={loading}>
                {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
              </button>

              {!isSignUp && (
                <div className="forgot-password-link">
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setError('');
                      setMessage('');
                    }}
                    className="link-btn"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </form>

            <div className="auth-switch">
              <p>
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setError('');
                    setMessage('');
                  }}
                  className="link-btn"
                >
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </button>
              </p>
            </div>

            <div className="back-link">
              <Link to="/">Back to App</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
